// Unpublished Work © 2024 Deere & Company.

import {
  DeprecatedDropdown,
  DeprecatedDropdownButton,
  DeprecatedDropdownItem,
  DeprecatedDropdownMenu,
} from "@deere/fuel-react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ConfigContext } from "../../../../context/ConfigContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import {
  CATEGORIES,
  TOGGLES,
} from "../../../../pages/featureToggles/constants";
import { getShowCoreReturnStatus } from "../../../../utils/showCoreReturns";

const CoreMenu = ({ dealerAccountId }: { dealerAccountId: string }) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const { ENABLE_CORE_RETURNS } = useContext(ConfigContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const coreReturnsUrl = `/dealer/${dealerAccountId}/returns`;
  const allCoreReturnsUrl = `/dealer/${dealerAccountId}/returns/core`;
  const showCoreReturnStatusLegacyToggleValue = getShowCoreReturnStatus();
  const showCoreReturnsToggle =
    showCoreReturnStatusLegacyToggleValue !== undefined
      ? showCoreReturnStatusLegacyToggleValue
      : isToggleEnabled(CATEGORIES.RETURNS, TOGGLES.ENABLE_CORE_RETURNS);

  const dropDownItemStyle = {
    borderWidth: "0px 0px 0px 3px",
    borderStyle: "solid",
    borderColor: "transparent",
  };

  const dropDownHoverStyle = {
    ".MenuItem-link:hover": {
      color: theme.tokens.FuelColorBrandPrimary,
      backgroundColor: theme.tokens.FuelColorAlertSuccessBackground,
      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
    },
  };

  return (
    <Box sx={dropDownHoverStyle}>
      <Link
        to={coreReturnsUrl}
        style={{
          textDecoration: "none",
        }}
      >
        <DeprecatedDropdownItem
          style={dropDownItemStyle}
          data-testid="returns-dropdownitem"
        >
          {t("header.navItem.returns.allReturns")}
        </DeprecatedDropdownItem>
      </Link>
      {ENABLE_CORE_RETURNS && showCoreReturnsToggle && (
        <Link
          to={allCoreReturnsUrl}
          style={{
            textDecoration: "none",
          }}
        >
          <DeprecatedDropdownItem
            style={dropDownItemStyle}
            data-testid="coreReturns-dropdownitem"
          >
            {t("header.navItem.returns.coreReturns")}
          </DeprecatedDropdownItem>
        </Link>
      )}
    </Box>
  );
};

export const ReturnsMenu = ({
  dealerAccountId,
  selected = false,
}: {
  dealerAccountId: string;
  selected?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation("common");

  return (
    <DeprecatedDropdown>
      <DeprecatedDropdownButton
        variant="text"
        data-testid="returns-drop-down"
        style={{
          padding: "20px 8px",
          color: theme.tokens.FuelColorGray600,
          borderRadius: "0px",
          ...(selected && {
            color: theme.tokens.FuelColorBrandPrimary,
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            borderBottom: `3px solid ${theme.tokens.FuelColorBrandPrimary}`,
          }),
        }}
      >
        {t("header.navItem.returns")}
      </DeprecatedDropdownButton>
      <DeprecatedDropdownMenu
        className="returns-menu-dropdown"
        style={{ minWidth: "max-content" }}
      >
        <Box sx={{ display: "flex" }}>
          <CoreMenu dealerAccountId={dealerAccountId} />
        </Box>
      </DeprecatedDropdownMenu>
    </DeprecatedDropdown>
  );
};
